import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { appleAuthHelpers } from 'react-apple-signin-auth';
import ApiService from '../../services/api-service';
import { UserContext } from '../../context/userContext';
import { APPLE_CLIENT_ID, APPLE_REDIRECT_URI } from '../../constants/api';
import { getUserId } from '../../constants/function';
import cookieStorage from '../../services/cookie-storage';
import { v4 as uuidv4 } from 'uuid';
import { Images } from '../../assets/common';
import LoadingScreen from '../../components/LoadingScreen';
import RobotStage from './RobotStage';
import { Manifesto, GoLive, Roles, PowerPlaysListing, ClosingCta } from './LandingSections';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { getAppStoreUrl } from '../../utils/mobileDetection';
import { copyInstallLink } from '../../utils/installHandoff';
import SEO from '../../components/SEO';
import './index.scss';

const fallbackLogo = require('../../assets/common/resources/SIKI_Beta.png');
const fallbackLoading = {
  icon: require('../../assets/common/resources/Sikibot.png'),
  alt: 'Loading',
};

function getServerUrl() {
  const h = window.location.hostname;
  if (h === 'siki.io' || h === 'www.siki.io' || h === 'memehouse.siki.io') return 'https://core.siki.io';
  if (h.includes('staging')) return 'https://staging-core.siki.io';
  if (h.includes('dev')) return 'https://dev-core.siki.io';
  return 'http://localhost:4041';
}

const SERVER_URL = getServerUrl();

const DesktopLanding = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [isLoading, setIsLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [liveViewers, setLiveViewers] = useState(248);
  const [powerPlays, setPowerPlays] = useState([]);
  const history = useHistory();
  const { setUser } = useContext(UserContext);
  const { getCurrentBranding, getLoadingConfig, currentThemeName } = useAppTheme();
  const isMemehouse = currentThemeName === 'memehouse';
  const platformName = isMemehouse ? 'Memehouse' : 'SIKI';
  const brandingDefaults = { logo: fallbackLogo, logoAlt: `${platformName} Logo` };
  const currentBranding = getCurrentBranding ? getCurrentBranding() : brandingDefaults;
  const landingBranding = {
    ...brandingDefaults,
    ...currentBranding,
  };
  const homeScreens = landingBranding.homeScreens || {};
  const screens = {
    app1: homeScreens.app1 || require('../../assets/mobile-screenshots/screen1.webp'),
    app2: homeScreens.app2 || require('../../assets/mobile-screenshots/screen2.webp'),
    app3: homeScreens.app3 || require('../../assets/mobile-screenshots/screen3.webp'),
    app4: homeScreens.app4 || require('../../assets/mobile-screenshots/screen4.webp'),
  };
  const themeLoading = getLoadingConfig ? getLoadingConfig() : {};
  const loadingAssets = {
    ...fallbackLoading,
    ...themeLoading,
  };

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, []);

  // Fetch PowerPlays
  useEffect(() => {
    fetch(SERVER_URL + '/public/powerplays')
      .then(res => res.json())
      .then(data => {
        if (data.ok && data.result) setPowerPlays(data.result);
      })
      .catch(() => {});
  }, []);

  // Animate live viewer count
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveViewers(prev => prev + Math.floor(Math.random() * 3) + 1);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const navigateToSection = (section) => {
    setActiveSection(section);
    setIsMobileNavOpen(false);
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getUserProfile = async (token) => {
    try {
      const response = await ApiService.USER_PROFILE(token);
      const userData = JSON.parse(localStorage.getItem("userData"));
      userData["userData"] = response.result;
      localStorage.setItem("userData", JSON.stringify(userData));
      localStorage.setItem("isWhiteListed", response.result["isWhiteListed"]);

      if (!response.result["gender"] || response.result["gender"] === "" || !response.result["birthYear"]) {
        window.location.href = '/signup';
      } else if (response.result["isWhiteListed"]) {
        window.location.href = '/app/home';
      } else {
        window.location.href = '/WaitList';
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      window.location.href = '/';
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    setLoginLoading(true);
    try {
      const response = await ApiService.googleSignInWithToken({
        idToken: credentialResponse.credential
      });

      if (response && response.result) {
        setUser(response.result);
        localStorage.setItem("userData", JSON.stringify(response.result));
        cookieStorage.createCookie("userId", getUserId());
        localStorage.setItem("token", response.result["token"]);
        localStorage.setItem("sessionId", uuidv4());
        localStorage.setItem("streamingBalance", response.result["userData"]["streamingBalance"]);

        cookieStorage.createCookie("Authorization", response.result["token"], 1);
        cookieStorage.createCookie("SikiAccounts", JSON.stringify(response.result["accounts"]), 1);
        cookieStorage.createCookie("swapObject", JSON.stringify(response.result["swapObject"]), 1);

        await getUserProfile(response.result["token"]);
      }
    } catch (error) {
      console.error('Google login error:', error);
      setLoginLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setLoginLoading(true);
    try {
      const response = await appleAuthHelpers.signIn({
        authOptions: {
          clientId: APPLE_CLIENT_ID,
          scope: 'email name',
          redirectURI: APPLE_REDIRECT_URI,
          state: 'state',
          nonce: 'nonce',
          usePopup: true,
        },
        onError: (error) => console.error('Apple login error:', error),
      });

      if (response && response.authorization) {
        let payload;
        if (response.user) {
          payload = {
            idToken: response.authorization.id_token,
            firstName: response.user.name?.firstName,
            lastName: response.user.name?.lastName,
          };
        } else {
          payload = {
            idToken: response.authorization.id_token,
          };
        }

        const apiResponse = await ApiService.appleSignInWithToken(payload);

        if (apiResponse && apiResponse.result) {
          setUser(apiResponse.result);
          localStorage.setItem("userData", JSON.stringify(apiResponse.result));
          cookieStorage.createCookie("userId", getUserId());
          localStorage.setItem("token", apiResponse.result["token"]);
          localStorage.setItem("sessionId", uuidv4());
          localStorage.setItem("streamingBalance", apiResponse.result["userData"]["streamingBalance"]);

          cookieStorage.createCookie("Authorization", apiResponse.result["token"], 1);
          cookieStorage.createCookie("SikiAccounts", JSON.stringify(apiResponse.result["accounts"]), 1);
          cookieStorage.createCookie("swapObject", JSON.stringify(apiResponse.result["swapObject"]), 1);

          await getUserProfile(apiResponse.result["token"]);
        }
      }
    } catch (error) {
      console.error('Apple login error:', error);
      setLoginLoading(false);
    }
  };

  const renderContent = () => {
    return (
      <div className="desktop-sections">
        {/* ════════ HOME / HERO ════════ */}
        <section id="home" className="hero-section">
          <div className="hero-content">
            <div className="hero-stage">
              <RobotStage wordmark={platformName.toUpperCase()} />

              <div className="hero-grid">
                {/* Hero Headline */}
                <div className="hero-main">
                  <div className="hero-eyebrow">REAL-TIME MUSIC ECONOMY</div>
                  <h1 className="hero-title">
                    The Future<br />of Music Is<br />
                    <span className="hero-title-accent">Real Time</span>
                  </h1>
                  <p className="hero-subtitle">Artists, curators, and fans earn royalties for every stream — paid in real time, not months later.</p>

                  <div className="live-stat">
                    <div className="live-stat-label">LIVE STREAM ROYALTIES</div>
                    <div className="live-stat-row">
                      <span className="live-stat-value">{liveViewers.toLocaleString()}</span>
                      <span className="live-stat-unit">viewers</span>
                      <span className="live-stat-eq">=</span>
                      <span className="live-stat-value">{liveViewers.toLocaleString()}</span>
                      <span className="live-stat-unit">streams / song</span>
                    </div>
                  </div>
                </div>

                {/* Login Panel */}
                <div className="login-section">
                  <div className="login-kicker">MEMBER ACCESS</div>
                  <h2>Welcome back</h2>
                  <p className="login-subtext">Log in to your studio</p>

                  {loginLoading && (
                    <LoadingScreen message="Signing in..." />
                  )}

                  {!loginLoading && (
                    <div className="login-buttons">
                      <div className="login-action-btn login-action-btn--google" style={{ position: 'relative' }}>
                        <img
                          src={Images.google}
                          alt="Google"
                          className="login-icon"
                        />
                        <span>Continue with Google</span>
                        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0 }}>
                          <GoogleLogin
                            onSuccess={(credentialResponse) => handleGoogleLogin(credentialResponse)}
                            onError={() => {
                              console.error('Google Login Failed');
                            }}
                            width="400"
                          />
                        </div>
                      </div>

                      <div className="login-action-btn login-action-btn--apple" onClick={handleAppleLogin}>
                        <img
                          src={Images.apple_icon}
                          alt="Apple"
                          className="login-icon apple-icon"
                        />
                        <span>Continue with Apple</span>
                      </div>
                    </div>
                  )}

                  <p className="login-terms">
                    By continuing, you agree to {platformName}'s{' '}
                    <a href="/TermsAndCondition">Terms of Service</a> and{' '}
                    <a href="/PrivacyPolicy">Privacy Policy</a>
                  </p>
                </div>
              </div>

              <div className="hero-scroll-cue" aria-hidden="true">SCROLL</div>
            </div>

            <Manifesto platformName={platformName} screen={screens.app1} />
          </div>
        </section>

        <GoLive platformName={platformName} screens={screens} />
        <Roles />
        <PowerPlaysListing powerPlays={powerPlays} />
        <ClosingCta
          platformName={platformName}
          onAppStore={() => { copyInstallLink().then(() => { window.location.href = getAppStoreUrl('ios'); }); }}
          onPlayStore={() => { window.location.href = getAppStoreUrl('android'); }}
        />
      </div>
    );
  };

  return (
    <div className={`desktop-landing ${isLoading ? 'loading' : 'loaded'}`}>
      <SEO
        title="Stream Music & Earn Real-Time Royalties"
        description="Upload your music, go live, and earn royalties from every stream — paid in real time, not months later. The creator-first platform for independent artists and playlistors."
        path="/"
      />
      {isLoading && (
        <div className="loading-screen">
          <div className="loader">
            <div className="loader-ring"></div>
            <img src={loadingAssets.icon} alt={loadingAssets.alt || 'Loading'} className="loader-logo" />
          </div>
        </div>
      )}

      <div className="desktop-landing__container">
        <header className="desktop-landing__header">
          <img src={landingBranding.logo} alt={landingBranding.logoAlt || platformName} className="logo" />
          <div className="header-actions">
            <nav className="desktop-nav">
              <button onClick={() => navigateToSection('home')} className={activeSection === 'home' ? 'active' : ''}>
                Home
              </button>
              <button onClick={() => navigateToSection('features')} className={activeSection === 'features' ? 'active' : ''}>
                Go Live
              </button>
              <button onClick={() => navigateToSection('how')} className={activeSection === 'how' ? 'active' : ''}>
                Who It's For
              </button>
              <button onClick={() => navigateToSection('powerplays')} className={activeSection === 'powerplays' ? 'active' : ''}>
                PowerPlays
              </button>
            </nav>
            <button
              className={`mobile-nav-toggle ${isMobileNavOpen ? 'open' : ''}`}
              onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
              aria-label="Toggle navigation menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </header>
        <div className={`mobile-nav-overlay ${isMobileNavOpen ? 'open' : ''}`} onClick={() => setIsMobileNavOpen(false)}></div>
        <div className={`mobile-nav-drawer ${isMobileNavOpen ? 'open' : ''}`}>
          <nav>
            <button onClick={() => navigateToSection('home')} className={activeSection === 'home' ? 'active' : ''}>
              Home
            </button>
            <button onClick={() => navigateToSection('features')} className={activeSection === 'features' ? 'active' : ''}>
              Go Live
            </button>
            <button onClick={() => navigateToSection('how')} className={activeSection === 'how' ? 'active' : ''}>
              Who It's For
            </button>
            <button onClick={() => navigateToSection('powerplays')} className={activeSection === 'powerplays' ? 'active' : ''}>
              PowerPlays
            </button>
          </nav>
        </div>

        <main className="desktop-landing__content">
          {renderContent()}
        </main>

        <footer className="desktop-landing__footer">
          <p className="tagline">{platformName} / Live video radio</p>
          <div className="footer-links">
            <a href="/PrivacyPolicy" className="footer-link">Privacy Policy</a>
            <a href="/TermsAndCondition" className="footer-link">Terms of Service</a>
          </div>
          <p className="copyright">© 2021–2026 {platformName}. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default DesktopLanding;
